import type { Club, SourceId } from '../../shared/types'

export interface Batch {
  source: SourceId
  clubIds: string[]
}

/**
 * A Worker invocation may make at most 50 subrequests on the free plan, and an
 * uncached week across every club needs more than that. So the client asks in
 * pieces: one request per source, split further when a single source would
 * blow the budget on its own.
 *
 * Kept comfortably under the real cap to leave room for the cache's own reads
 * and writes, which count too.
 */
const BUDGET = 34

/** Roughly how many upstream calls one club costs for `days` days. */
function cost(club: Club, days: number): number {
  switch (club.ref.kind) {
    // A config call per court, a session, then one live call per court per day
    // inside the portal's 7-day horizon - the only data we trust.
    case 'simplifica':
      return club.courts.length * (Math.min(days, 8) + 1) + 1
    // Court metadata per court, plus one shared bookings query.
    case 'field':
      return club.courts.length + 1
    // One call per day.
    case 'playtomic':
    case 'tiesports':
      return days
    // One call per day, plus a session handshake.
    case 'matchpoint':
      return days + 1
    case 'manual':
      return 0
  }
}

/** Greedy first-fit: keeps batches few while none exceeds the budget. */
export function planBatches(clubs: Club[], days: number): Batch[] {
  const bySource = new Map<SourceId, Club[]>()
  for (const club of clubs) {
    if (!club.live) continue
    const bucket = bySource.get(club.source)
    if (bucket) bucket.push(club)
    else bySource.set(club.source, [club])
  }

  const batches: Batch[] = []
  for (const [source, sourceClubs] of bySource) {
    let current: string[] = []
    let spent = 0

    for (const club of sourceClubs) {
      const c = cost(club, days)
      if (current.length > 0 && spent + c > BUDGET) {
        batches.push({ source, clubIds: current })
        current = []
        spent = 0
      }
      current.push(club.id)
      spent += c
    }
    if (current.length > 0) batches.push({ source, clubIds: current })
  }

  return batches
}
